import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { PublicFooter } from "@/components/legal/public-footer";
import { multiResumeBrand } from "@/utils/multiresume-brand";

import { GuideStructuredData } from "./guide-structured-data";
import {
  CHECK_AI_RESUME_EDITS_GUIDE_PATH,
  guideTitle,
  guidesDirectoryMessages,
  MANAGE_RESUME_VERSIONS_GUIDE_PATH,
} from "./guides-copy";

const HELP_SCOUT_URL = "https://builtin.com/job/sr-product-analyst/9746007";
const CDA_URL = "https://www.cda.cn/view/206987.html";
const TAILOR_RESUME_GUIDE_CANONICAL_URL = "https://multiresu.me/guides/tailor-resume-to-job-description";

export const guideMessages = {
  title: guideTitle,
  metadataDescription: msg({
    id: "growth.c01.metadata-description",
    message:
      "Learn how to tailor your resume to a job description with a worked example: identify requirements, rewrite relevant experience, and check the final resume.",
  }),
  introOne: msg({
    id: "growth.c01.intro-one",
    message:
      'A resume can be accurate and still bury the reason you\'re a good fit. You spend four bullets on dashboards you maintain every week, then reduce a relevant project to "Supported onboarding improvements." The person reading your application has to guess what that support involved.',
  }),
  introTwo: msg({
    id: "growth.c01.intro-two",
    message:
      "Sending that same resume to every opening leaves the problem untouched. A team hiring for routine reporting and a team trying to understand why customers abandon a product will look for different evidence. Your experience may cover both, but the space and detail you give each part should change with the job.",
  }),
  introThree: msg({
    id: "growth.c01.intro-three",
    message:
      "We'll work through one example: Jordan Ellis, a fictional analyst with about five years of experience at business software companies. The job posting is real; Jordan's employment history and projects are invented for this guide. Jordan has useful product experience hidden in a resume that mostly describes reporting duties. The task is to bring that work into view without adding claims the candidate couldn't explain in an interview.",
  }),
  relatedManagePrefix: msg({
    id: "growth.c01.related-manage-prefix",
    message: "Applying to several roles? See how to",
  }),
  relatedManageLink: msg({
    id: "growth.c01.related-manage-link",
    message: "manage multiple resume versions",
  }),
  relatedManageSuffix: msg({
    id: "growth.c01.related-manage-suffix",
    message: "so you can find the exact file you sent.",
  }),
  relatedFactCheckPrefix: msg({
    id: "growth.c01.related-fact-check-prefix",
    message: "If you used AI to rewrite a section,",
  }),
  relatedFactCheckLink: msg({
    id: "growth.c01.related-fact-check-link",
    message: "check the edits against your original facts",
  }),
  relatedFactCheckSuffix: msg({
    id: "growth.c01.related-fact-check-suffix",
    message: "before submitting.",
  }),
  onThisPage: msg({ id: "growth.c01.on-this-page", message: "On this page" }),
  tocOne: msg({ id: "growth.c01.toc-one", message: "Read the job description for the work you'll be doing" }),
  tocTwo: msg({
    id: "growth.c01.toc-two",
    message: "Rewrite the relevant experience from what actually happened",
  }),
  tocThree: msg({ id: "growth.c01.toc-three", message: "Make room for the strongest evidence" }),

  sectionOneHeading: msg({
    id: "growth.c01.section-one-heading",
    message: "1. Read the job description for the work you'll be doing",
  }),
  sectionOneParagraphOne: msg({
    id: "growth.c01.section-one-paragraph-one",
    message:
      "Start with the responsibilities. Before changing a sentence in your resume, write down what the person hired will need to do. Then check the requirements and any practical conditions, such as location or work authorization, that affect whether you can take the job.",
  }),
  sectionOneParagraphTwo: msg({
    id: "growth.c01.section-one-paragraph-two",
    message: "For this example, we're using Help Scout's Sr. Product Analyst job description, reproduced on Built In.",
  }),
  sectionOneSourceLink: msg({
    id: "growth.c01.section-one-source-link",
    message: "Help Scout's Sr. Product Analyst listing on Built In",
  }),
  sectionOneSourceNote: msg({
    id: "growth.c01.section-one-source-note",
    message:
      "The original listing is no longer available, and Built In marks this role as removed. We use the historical description for this exercise.",
  }),
  tableHeaderWork: msg({ id: "growth.c01.table-header-work", message: "Work described in the posting" }),
  tableHeaderMeaning: msg({ id: "growth.c01.table-header-meaning", message: "What it means for this resume" }),
  tableHeaderEvidence: msg({ id: "growth.c01.table-header-evidence", message: "What to show" }),
  tableRowOneLabel: msg({
    id: "growth.c01.table-row-one-label",
    message: "Manage Mixpanel tracking and event definitions",
  }),
  tableRowOneMeaning: msg({
    id: "growth.c01.table-row-one-meaning",
    message: "A tracking problem you investigated",
  }),
  tableRowOneEvidence: msg({
    id: "growth.c01.table-row-one-evidence",
    message: "The changes you specified and how you validated them",
  }),
  tableRowTwoLabel: msg({
    id: "growth.c01.table-row-two-label",
    message: "Examine product usage, including activation and retention",
  }),
  tableRowTwoMeaning: msg({
    id: "growth.c01.table-row-two-meaning",
    message: "An analysis that explained where customers got stuck",
  }),
  tableRowTwoEvidence: msg({
    id: "growth.c01.table-row-two-evidence",
    message: "A funnel or cohort example with the decision it supported",
  }),
  tableRowThreeLabel: msg({
    id: "growth.c01.table-row-three-label",
    message: "Work with product managers and engineers on recommendations",
  }),
  tableRowThreeMeaning: msg({
    id: "growth.c01.table-row-three-meaning",
    message: "A decision someone made using your findings",
  }),
  tableRowThreeEvidence: msg({
    id: "growth.c01.table-row-three-evidence",
    message: "Your contribution and the team's division of work",
  }),
  tableRowFourLabel: msg({ id: "growth.c01.table-row-four-label", message: "Plan and analyze experiments" }),
  tableRowFourMeaning: msg({
    id: "growth.c01.table-row-four-meaning",
    message: "A test where you helped set the measure of success and interpreted the results",
  }),
  tableRowFourEvidence: msg({
    id: "growth.c01.table-row-four-evidence",
    message: "The measure, assignment, and readout—without inventing a result",
  }),
  sectionOneParagraphThree: msg({
    id: "growth.c01.section-one-paragraph-three",
    message:
      "The posting asks for three to five years in SaaS product analytics, strong SQL, and hands-on Mixpanel experience. Hex experience appears among the optional qualifications. Those distinctions matter: Jordan can show SQL and Mixpanel in actual project bullets, while listing the BI tool they've used, Looker, without pretending they've used Hex.",
  }),
  sectionOneParagraphFour: msg({
    id: "growth.c01.section-one-paragraph-four",
    message:
      "Read your resume beside these notes. Jordan's weekly reporting work deserves some space, but an investigation into stalled onboarding answers more of the questions in the table. That project should move up. A separate experiment can demonstrate another requirement without making the onboarding project carry every claim.",
  }),
  sectionOneParagraphFive: msg({
    id: "growth.c01.section-one-paragraph-five",
    message:
      "For your own application, note a specific example beside each important responsibility. An empty space is worth noticing. You may have omitted relevant work from your resume, or you may lack that experience. Check your records before deciding which it is. Changing the wording cannot fill a genuine gap.",
  }),

  sectionTwoHeading: msg({
    id: "growth.c01.section-two-heading",
    message: "2. Rewrite the relevant experience from what actually happened",
  }),
  sectionTwoParagraphOne: msg({
    id: "growth.c01.section-two-paragraph-one",
    message:
      "Jordan's original bullet reads:\n\nSupported onboarding improvements through data analysis and reporting.",
  }),
  comparisonBeforeLabel: msg({ id: "growth.c01.comparison-before-label", message: "Original bullet" }),
  comparisonBefore: msg({
    id: "growth.c01.comparison-before",
    message: "Supported onboarding improvements through data analysis and reporting.",
  }),
  comparisonAfterLabel: msg({ id: "growth.c01.comparison-after-label", message: "Two clearer bullets" }),
  comparisonAfterOne: msg({
    id: "growth.c01.comparison-after-one",
    message:
      "Audited activation tracking in SQL and Mixpanel; defined activation with the PM as a first successful workflow run within seven days of workspace creation and validated engineering's tracking changes.",
  }),
  comparisonAfterTwo: msg({
    id: "growth.c01.comparison-after-two",
    message:
      "Identified the largest onboarding drop-off at integration setup through funnel analysis and support-ticket review; recommended clearer connection guidance, which the PM prioritized on the roadmap.",
  }),
  sectionTwoParagraphTwo: msg({
    id: "growth.c01.section-two-paragraph-two",
    message:
      "It doesn't tell us what was wrong, what Jordan did, or what anyone did with the analysis. Before rewriting it, we need the project details. In this fictional case, those details are:",
  }),
  sectionTwoDetailOne: msg({
    id: "growth.c01.section-two-detail-one",
    message:
      "Jordan's employer sold software that let teams build automated workflows. The product dashboard counted a workspace as activated when someone saved a workflow, even if it never ran. Jordan checked the SQL behind the dashboard and compared Mixpanel events with execution records. With the product manager, they agreed to measure activation as a workspace's first successful workflow run within seven days of creation. Jordan documented the definition and tracking requirements; engineers implemented the tracking changes, which Jordan then checked.",
  }),
  sectionTwoDetailTwo: msg({
    id: "growth.c01.section-two-detail-two",
    message:
      "With the corrected data, Jordan traced the steps from workspace creation to a successful run. The biggest drop was between saving a workflow and connecting the external app it needed. Support tickets also described confusion about connection permissions. Jordan recommended changes to the setup guidance, and the product manager put that work on the onboarding roadmap. Design and engineering would handle the interface work.",
  }),
  sectionTwoDetailThree: msg({
    id: "growth.c01.section-two-detail-three",
    message:
      "The project record stops at the accepted recommendation. It contains no measured lift in activation, retention, or revenue.",
  }),
  sectionTwoParagraphThree: msg({
    id: "growth.c01.section-two-paragraph-three",
    message:
      "The first bullet shows measurement work. The second gives the product question, the evidence Jordan used, and the decision that followed. Both are more useful than the original line because the reader can see what Jordan contributed.",
  }),
  sectionTwoParagraphFour: msg({
    id: "growth.c01.section-two-paragraph-four",
    message:
      'The ownership is also clearer. Jordan can say they audited the tracking and recommended a change. "Led an onboarding redesign" would claim responsibility for work done by the PM, designer, and engineers. Likewise, "increased activation" would need a result the project record doesn\'t contain. A recommendation accepted for the roadmap is a concrete outcome worth stating on its own.',
  }),
  sectionTwoParagraphFive: msg({
    id: "growth.c01.section-two-paragraph-five",
    message:
      "When your project does have a measured result, keep the definition attached. Say what changed, over what period, and what the comparison was. If the only confirmed outcome was a decision, a corrected measurement, or a completed deliverable, use that. Adding a percentage because the bullet looks stronger with one creates a claim you'll have to defend later.",
  }),
  sectionTwoParagraphSix: msg({
    id: "growth.c01.section-two-paragraph-six",
    message:
      'You can also replace internal shorthand with language an outside reader understands. Jordan\'s colleagues might call this the "first-run cleanup." On a resume, "activation tracking" and "onboarding drop-off" explain the work. Keep your actual job title and tools, though. Changing "Data Analyst" to "Senior Product Analyst" would alter the employment record, even if the new title matches the opening.',
  }),

  sectionThreeHeading: msg({
    id: "growth.c01.section-three-heading",
    message: "3. Make room for the strongest evidence",
  }),
  sectionThreeParagraphOne: msg({
    id: "growth.c01.section-three-paragraph-one",
    message:
      "Two improved bullets won't help much if they're still below a long list of less relevant duties. Put them near the top of Jordan's current role, then consider what else earns space.",
  }),
  sectionThreeParagraphTwo: msg({
    id: "growth.c01.section-three-paragraph-two",
    message:
      "Jordan also worked on an A/B test of a setup checklist. One bullet can describe the experiment design and readout. The recurring dashboards need only one line explaining what the product team used them for. That leaves four bullets under the current role, with most of the detail going to the work this application needs.",
  }),
  sectionThreeParagraphThree: msg({
    id: "growth.c01.section-three-paragraph-three",
    message:
      "Keep jobs in reverse chronological order. Within a role, reorder the bullets by relevance. Jordan's earlier scheduling-software role can be shorter, but it should still show the employer, title, dates, and a couple of substantive contributions. Don't pull an older project into the current job to get it higher on the page.",
  }),
  sectionThreeParagraphFour: msg({
    id: "growth.c01.section-three-paragraph-four",
    message:
      "For this particular example, I'd try one readable page first: two jobs, a compact skills section, and education near the bottom. The complete example is short enough to try that without losing the important work. Your resume may need more room. If a second page carries distinct, relevant experience, keep it; if it mostly repeats responsibilities, edit those first. Follow any length or format instructions in the application.",
  }),
  sectionThreeParagraphFive: msg({
    id: "growth.c01.section-three-paragraph-five",
    message:
      'A summary is optional. "Results-driven professional with a proven track record" takes space without explaining Jordan\'s work. If you use a summary, make it specific enough to help the reader place your experience, then let the job entries provide the detail. Avoid repeating the same project in a summary, an experience section, and a separate project section.',
  }),
  checklistHeading: msg({ id: "growth.c01.checklist-heading", message: "Before you submit" }),
  checklistOne: msg({
    id: "growth.c01.checklist-one",
    message:
      "Can the reader find evidence for the job's main responsibilities without reconstructing your career from a skills list?",
  }),
  checklistTwo: msg({
    id: "growth.c01.checklist-two",
    message:
      "Does each edited bullet say what you personally did, with team ownership and any limits on the result intact?",
  }),
  checklistThree: msg({
    id: "growth.c01.checklist-three",
    message: "Are the tools, titles, dates, and numbers consistent with your records?",
  }),
  checklistFour: msg({
    id: "growth.c01.checklist-four",
    message: "Have you kept enough detail about the relevant work while cutting repetition elsewhere?",
  }),
  checklistFive: msg({
    id: "growth.c01.checklist-five",
    message:
      "Is the text comfortable to read, with no broken bullets, awkward page breaks, or missing contact details?",
  }),
  checklistSix: msg({
    id: "growth.c01.checklist-six",
    message: "Does the file meet the employer's instructions, and is it the version you intended to attach?",
  }),
  productCall: msg({
    id: "growth.c01.product-call",
    message:
      "You can do this with your existing resume and a copy of the job description. If you use MultiResume, you can maintain a Complete Resume and create a Job Version for a particular role, reviewing the changes before you apply. Registration currently requires an invitation; this guide can be followed without an account.",
  }),
  productHomeLink: msg({
    id: "growth.c01.product-home-link",
    message: "Visit the MultiResume homepage.",
  }),

  fictionalResumeLabel: msg({ id: "growth.c01.fictional-resume-label", message: "Fictional resume after tailoring" }),
  fictionalResumeNote: msg({
    id: "growth.c01.fictional-resume-note",
    message: "This is a teaching example, not a real applicant or a MultiResume customer result.",
  }),
  resumeName: msg({ id: "growth.c01.resume-name", message: "Jordan Ellis" }),
  resumeContact: msg({ id: "growth.c01.resume-contact", message: "Product Analyst | jordan.ellis@example.com" }),
  experienceHeading: msg({ id: "growth.c01.resume-experience-heading", message: "Experience" }),
  currentRole: msg({ id: "growth.c01.resume-current-role", message: "Product Analyst, workflow software company" }),
  currentRoleDates: msg({ id: "growth.c01.resume-current-role-dates", message: "September 2023 - Present" }),
  currentBulletOne: msg({
    id: "growth.c01.resume-current-bullet-one",
    message:
      "Audited activation tracking in SQL and Mixpanel; defined activation with the PM as a first successful workflow run within seven days of workspace creation and validated engineering's tracking changes.",
  }),
  currentBulletTwo: msg({
    id: "growth.c01.resume-current-bullet-two",
    message:
      "Identified the largest onboarding drop-off at integration setup through funnel analysis and support-ticket review; recommended clearer connection guidance, which the PM prioritized on the roadmap.",
  }),
  currentBulletThree: msg({
    id: "growth.c01.resume-current-bullet-three",
    message:
      "Designed the analysis for a setup-checklist A/B test, including workspace-level assignment, sample-size planning, and success measures; delivered a Python readout of activation and support-ticket volume for the rollout decision.",
  }),
  currentBulletFour: msg({
    id: "growth.c01.resume-current-bullet-four",
    message:
      "Built Looker dashboards and reusable SQL queries for weekly product reviews, with documented definitions for activation and feature adoption.",
  }),
  previousRole: msg({ id: "growth.c01.resume-previous-role", message: "Data Analyst, scheduling software company" }),
  previousRoleDates: msg({ id: "growth.c01.resume-previous-role-dates", message: "June 2021 - August 2023" }),
  previousBulletOne: msg({
    id: "growth.c01.resume-previous-bullet-one",
    message:
      "Analyzed booking-feature usage by customer segment in SQL, helping product managers choose accounts for follow-up research.",
  }),
  previousBulletTwo: msg({
    id: "growth.c01.resume-previous-bullet-two",
    message:
      "Replaced recurring spreadsheet extracts with scheduled queries and a Looker dashboard for weekly usage reporting; documented filters and checked results against source records.",
  }),
  skillsHeading: msg({ id: "growth.c01.resume-skills-heading", message: "Skills" }),
  skillsOne: msg({
    id: "growth.c01.resume-skills-one",
    message: "SQL (BigQuery, PostgreSQL), Mixpanel, Looker, Python (pandas, SciPy)",
  }),
  skillsTwo: msg({
    id: "growth.c01.resume-skills-two",
    message: "Product event tracking, funnel and cohort analysis, A/B test design and analysis",
  }),
  educationHeading: msg({ id: "growth.c01.resume-education-heading", message: "Education" }),
  educationOne: msg({ id: "growth.c01.resume-education-one", message: "BS in Statistics, May 2021" }),
  educationTwo: msg({
    id: "growth.c01.resume-education-two",
    message: "University name omitted for this fictional example",
  }),

  profileHeading: msg({ id: "growth.c01.zh.resume-profile-heading", message: "Profile" }),
  desiredRole: msg({ id: "growth.c01.zh.resume-desired-role", message: "Target role: Growth Data Analyst" }),
  workYears: msg({
    id: "growth.c01.zh.resume-work-years",
    message: "About five years of experience as of September 2026",
  }),
  chineseEducation: msg({
    id: "growth.c01.zh.resume-education",
    message: "University name omitted | Statistics | Bachelor's degree | 2017.09—2021.06",
  }),
  chineseSkillsHeading: msg({ id: "growth.c01.zh.resume-skills-heading", message: "Professional skills" }),
  chineseSkillsOne: msg({
    id: "growth.c01.zh.resume-skills-one",
    message:
      "SQL: joins, window functions, and common business metric calculations; Hive for user behavior and order data.",
  }),
  chineseSkillsTwo: msg({
    id: "growth.c01.zh.resume-skills-two",
    message: "Python: Pandas for data cleaning, metric checks, and recurring reports.",
  }),
  chineseSkillsThree: msg({
    id: "growth.c01.zh.resume-skills-three",
    message: "Tableau and Excel: business dashboards, campaign review reports, and operating analysis.",
  }),
  chineseSkillsFour: msg({
    id: "growth.c01.zh.resume-skills-four",
    message:
      "Methods: channel comparison, funnel analysis, user segmentation, retention, repeat purchase, and experiment review.",
  }),
  chineseWorkHeading: msg({ id: "growth.c01.zh.resume-work-heading", message: "Work experience" }),
  chineseCurrentRole: msg({
    id: "growth.c01.zh.resume-current-role",
    message: "E-commerce company | Data Analyst | 2023.07—Present",
  }),
  chineseCurrentBulletOne: msg({
    id: "growth.c01.zh.resume-current-bullet-one",
    message:
      "Analyzed daily new-user registration, first-order, and repeat-purchase metrics in the app by channel and campaign.",
  }),
  chineseCurrentBulletTwo: msg({
    id: "growth.c01.zh.resume-current-bullet-two",
    message:
      "Maintained operating dashboards, checked tracking and order-data definitions, and handled product and operations data requests.",
  }),
  chineseCurrentBulletThree: msg({
    id: "growth.c01.zh.resume-current-bullet-three",
    message:
      "Participated in a new-customer conversion project, worked with product and engineering, and owned analysis and experiment review.",
  }),
  chineseCurrentBulletFour: msg({
    id: "growth.c01.zh.resume-current-bullet-four",
    message: "Wrote SQL and Python scripts to reduce repeated preparation in recurring reports.",
  }),
  chinesePreviousRole: msg({
    id: "growth.c01.zh.resume-previous-role",
    message: "Chain retail company | Operations Data Analyst | 2021.07—2023.06",
  }),
  chinesePreviousBulletOne: msg({
    id: "growth.c01.zh.resume-previous-bullet-one",
    message:
      "Prepared sales, gross-margin, and inventory data by store, region, and category for weekly and monthly reports.",
  }),
  chinesePreviousBulletTwo: msg({
    id: "growth.c01.zh.resume-previous-bullet-two",
    message:
      "Analyzed sales and member repeat-purchase changes before and after promotions for regional operations reviews.",
  }),
  chinesePreviousBulletThree: msg({
    id: "growth.c01.zh.resume-previous-bullet-three",
    message:
      "Checked differences among orders, refunds, and store reports and helped align common operating metric definitions.",
  }),
  chineseProjectsHeading: msg({ id: "growth.c01.zh.resume-projects-heading", message: "Projects" }),
  chineseProjectOneTitle: msg({
    id: "growth.c01.zh.resume-project-one-title",
    message: "New-customer first-order conversion analysis",
  }),
  chineseProjectOneMeta: msg({
    id: "growth.c01.zh.resume-project-one-meta",
    message: "2025.03—2025.06 | E-commerce company",
  }),
  chineseProjectOneBackground: msg({
    id: "growth.c01.zh.resume-project-one-background",
    message:
      "Background: New customers who claimed a coupon did not continue to add items to their carts; the team needed to locate the main drop-off and assess a change to coupon-use guidance.",
  }),
  chineseProjectOneWorkOne: msg({
    id: "growth.c01.zh.resume-project-one-work-one",
    message:
      "Used SQL to split the registration, coupon-claim, add-to-cart, and payment funnel by acquisition channel and found that some channels mainly lost users after coupon claim.",
  }),
  chineseProjectOneWorkTwo: msg({
    id: "growth.c01.zh.resume-project-one-work-two",
    message:
      "Combined customer-service feedback about difficulty finding eligible products with the analysis and proposed clearer guidance and a product-entry improvement.",
  }),
  chineseProjectOneWorkThree: msg({
    id: "growth.c01.zh.resume-project-one-work-three",
    message:
      "Worked with product and engineering; product owned the page design and engineering owned implementation and experiment assignment. Participated in setting experiment metrics, checked data definitions and groups, and analyzed the results.",
  }),
  chineseResult: msg({
    id: "growth.c01.zh.resume-project-one-result",
    message:
      "During the same observation period, the experimental group's seven-day post-registration first-order payment rate was 20.1%, compared with 18.2% for the concurrent control group, a difference of 1.9 percentage points.",
  }),
  chineseProjectTwoTitle: msg({
    id: "growth.c01.zh.resume-project-two-title",
    message: "Campaign review report automation",
  }),
  chineseProjectTwoMeta: msg({
    id: "growth.c01.zh.resume-project-two-meta",
    message: "2024.08—2024.10 | E-commerce company",
  }),
  chineseProjectTwoBackground: msg({
    id: "growth.c01.zh.resume-project-two-background",
    message:
      "Background: Operations needed weekly order and user data by channel and campaign, but the prior process required repeated exports, checks, and consolidation.",
  }),
  chineseProjectTwoWork: msg({
    id: "growth.c01.zh.resume-project-two-work",
    message:
      "Standardized query and report definitions, used SQL and Python to prepare the data, maintained the Tableau view, and checked fields and results with operations.",
  }),
  chineseProjectTwoResult: msg({
    id: "growth.c01.zh.resume-project-two-result",
    message:
      "Reduced manual preparation for the same weekly report from about two hours to about thirty minutes, with an analyst continuing to check anomalous data.",
  }),
} as const;

type GuideCopy = typeof guideMessages;

const tableRows = [
  [guideMessages.tableRowOneLabel, guideMessages.tableRowOneMeaning, guideMessages.tableRowOneEvidence],
  [guideMessages.tableRowTwoLabel, guideMessages.tableRowTwoMeaning, guideMessages.tableRowTwoEvidence],
  [guideMessages.tableRowThreeLabel, guideMessages.tableRowThreeMeaning, guideMessages.tableRowThreeEvidence],
  [guideMessages.tableRowFourLabel, guideMessages.tableRowFourMeaning, guideMessages.tableRowFourEvidence],
] as const;

function messageText(currentI18n: typeof i18n, message: MessageDescriptor) {
  return currentI18n._(message);
}

export function getTailorResumeGuideMetadata() {
  const title = messageText(i18n, guideMessages.title);
  const description = messageText(i18n, guideMessages.metadataDescription);

  return {
    title: `${title} - ${multiResumeBrand.appName}`,
    description,
    ogUrl: TAILOR_RESUME_GUIDE_CANONICAL_URL,
  };
}

function ResumeRole({
  currentI18n,
  heading,
  dates,
  bullets,
}: {
  currentI18n: typeof i18n;
  heading: MessageDescriptor;
  dates?: MessageDescriptor;
  bullets: readonly MessageDescriptor[];
}) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="font-semibold text-foreground">{messageText(currentI18n, heading)}</h4>
        {dates ? <p className="text-sm text-muted-foreground">{messageText(currentI18n, dates)}</p> : null}
      </div>
      <ul className="list-disc space-y-2 pl-5">
        {bullets.map((bullet) => (
          <li key={bullet.id}>{messageText(currentI18n, bullet)}</li>
        ))}
      </ul>
    </section>
  );
}

function EnglishResume({ currentI18n }: { currentI18n: typeof i18n }) {
  return (
    <div className="space-y-7">
      <div className="space-y-1 border-b border-border pb-5">
        <h3 className="text-2xl font-semibold text-foreground">{messageText(currentI18n, guideMessages.resumeName)}</h3>
        <p>{messageText(currentI18n, guideMessages.resumeContact)}</p>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.experienceHeading)}
        </h4>
        <ResumeRole
          currentI18n={currentI18n}
          heading={guideMessages.currentRole}
          dates={guideMessages.currentRoleDates}
          bullets={[
            guideMessages.currentBulletOne,
            guideMessages.currentBulletTwo,
            guideMessages.currentBulletThree,
            guideMessages.currentBulletFour,
          ]}
        />
        <ResumeRole
          currentI18n={currentI18n}
          heading={guideMessages.previousRole}
          dates={guideMessages.previousRoleDates}
          bullets={[guideMessages.previousBulletOne, guideMessages.previousBulletTwo]}
        />
      </div>

      <div className="space-y-2">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.skillsHeading)}
        </h4>
        <p>{messageText(currentI18n, guideMessages.skillsOne)}</p>
        <p>{messageText(currentI18n, guideMessages.skillsTwo)}</p>
      </div>

      <div className="space-y-2">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.educationHeading)}
        </h4>
        <p>{messageText(currentI18n, guideMessages.educationOne)}</p>
        <p>{messageText(currentI18n, guideMessages.educationTwo)}</p>
      </div>
    </div>
  );
}

function ChineseResume({ currentI18n }: { currentI18n: typeof i18n }) {
  return (
    <div className="space-y-7">
      <div className="space-y-1 border-b border-border pb-5">
        <h3 className="text-2xl font-semibold text-foreground">{messageText(currentI18n, guideMessages.resumeName)}</h3>
        <p>{messageText(currentI18n, guideMessages.profileHeading)}</p>
      </div>

      <div className="space-y-2">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.profileHeading)}
        </h4>
        <p>{messageText(currentI18n, guideMessages.desiredRole)}</p>
        <p>{messageText(currentI18n, guideMessages.workYears)}</p>
        <p>{messageText(currentI18n, guideMessages.chineseEducation)}</p>
      </div>

      <div className="space-y-2">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.chineseSkillsHeading)}
        </h4>
        <ul className="list-disc space-y-2 pl-5">
          {[
            guideMessages.chineseSkillsOne,
            guideMessages.chineseSkillsTwo,
            guideMessages.chineseSkillsThree,
            guideMessages.chineseSkillsFour,
          ].map((skill) => (
            <li key={skill.id}>{messageText(currentI18n, skill)}</li>
          ))}
        </ul>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.chineseWorkHeading)}
        </h4>
        <ResumeRole
          currentI18n={currentI18n}
          heading={guideMessages.chineseCurrentRole}
          bullets={[
            guideMessages.chineseCurrentBulletOne,
            guideMessages.chineseCurrentBulletTwo,
            guideMessages.chineseCurrentBulletThree,
            guideMessages.chineseCurrentBulletFour,
          ]}
        />
        <ResumeRole
          currentI18n={currentI18n}
          heading={guideMessages.chinesePreviousRole}
          bullets={[
            guideMessages.chinesePreviousBulletOne,
            guideMessages.chinesePreviousBulletTwo,
            guideMessages.chinesePreviousBulletThree,
          ]}
        />
      </div>

      <div className="space-y-5">
        <h4 className="text-lg font-semibold text-foreground">
          {messageText(currentI18n, guideMessages.chineseProjectsHeading)}
        </h4>
        <section className="space-y-2">
          <h5 className="font-semibold text-foreground">
            {messageText(currentI18n, guideMessages.chineseProjectOneTitle)}
          </h5>
          <p className="text-sm text-muted-foreground">
            {messageText(currentI18n, guideMessages.chineseProjectOneMeta)}
          </p>
          <p>{messageText(currentI18n, guideMessages.chineseProjectOneBackground)}</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{messageText(currentI18n, guideMessages.chineseProjectOneWorkOne)}</li>
            <li>{messageText(currentI18n, guideMessages.chineseProjectOneWorkTwo)}</li>
            <li>{messageText(currentI18n, guideMessages.chineseProjectOneWorkThree)}</li>
          </ul>
          <p>{messageText(currentI18n, guideMessages.chineseResult)}</p>
        </section>
        <section className="space-y-2">
          <h5 className="font-semibold text-foreground">
            {messageText(currentI18n, guideMessages.chineseProjectTwoTitle)}
          </h5>
          <p className="text-sm text-muted-foreground">
            {messageText(currentI18n, guideMessages.chineseProjectTwoMeta)}
          </p>
          <p>{messageText(currentI18n, guideMessages.chineseProjectTwoBackground)}</p>
          <p>{messageText(currentI18n, guideMessages.chineseProjectTwoWork)}</p>
          <p>{messageText(currentI18n, guideMessages.chineseProjectTwoResult)}</p>
        </section>
      </div>
    </div>
  );
}

function ComparisonCard({
  currentI18n,
  label,
  children,
  tone,
}: {
  currentI18n: typeof i18n;
  label: MessageDescriptor;
  children: React.ReactNode;
  tone: "muted" | "accent";
}) {
  return (
    <div
      className={`space-y-3 rounded-lg border p-5 ${tone === "accent" ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"}`}
    >
      <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {messageText(currentI18n, label)}
      </p>
      <div className="leading-relaxed text-foreground">{children}</div>
    </div>
  );
}

function GuideSection({
  id,
  heading,
  children,
}: {
  id: string;
  heading: MessageDescriptor;
  children: React.ReactNode;
}) {
  const { i18n: currentI18n } = useLingui();

  return (
    <section id={id} className="scroll-mt-24 space-y-6">
      <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {messageText(currentI18n, heading)}
      </h2>
      {children}
    </section>
  );
}

export function TailorResumeToJobDescriptionGuide() {
  const { i18n: currentI18n } = useLingui();
  const t = (message: MessageDescriptor) => messageText(currentI18n, message);
  const isEnglish = currentI18n.locale.toLowerCase().startsWith("en");
  const rows = isEnglish ? tableRows : tableRows.slice(0, 3);
  const sourceUrl = isEnglish ? HELP_SCOUT_URL : CDA_URL;

  return (
    <main id="main-content" className="min-h-svh bg-background pt-20">
      <GuideStructuredData
        headline={t(guideMessages.title)}
        description={t(guideMessages.metadataDescription)}
        url={TAILOR_RESUME_GUIDE_CANONICAL_URL}
        language={currentI18n.locale}
        directoryName={t(guidesDirectoryMessages.title)}
      />
      <article className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-12 lg:py-16">
        <nav aria-label={t(guidesDirectoryMessages.breadcrumb)} className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <li>
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href="/guides"
                data-phase6-entry="article-to-guides"
              >
                {t(guidesDirectoryMessages.title)}
              </a>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{t(guideMessages.title)}</li>
          </ol>
        </nav>

        <header className="space-y-5 border-b border-border pb-10">
          <h1 className="text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl">
            {t(guideMessages.title)}
          </h1>
          <div className="max-w-3xl space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>{t(guideMessages.introOne)}</p>
            <p>{t(guideMessages.introTwo)}</p>
            <p>{t(guideMessages.introThree)}</p>
          </div>
        </header>

        <nav aria-label={t(guideMessages.onThisPage)} className="my-10 rounded-lg border border-border bg-muted/20 p-5">
          <p className="text-sm font-semibold text-foreground">{t(guideMessages.onThisPage)}</p>
          <ol className="mt-3 space-y-2 text-sm">
            <li>
              <a
                className="font-medium text-foreground underline decoration-border underline-offset-4"
                href="#read-the-job-description"
              >
                {t(guideMessages.tocOne)}
              </a>
            </li>
            <li>
              <a
                className="font-medium text-foreground underline decoration-border underline-offset-4"
                href="#rewrite-relevant-experience"
              >
                {t(guideMessages.tocTwo)}
              </a>
            </li>
            <li>
              <a
                className="font-medium text-foreground underline decoration-border underline-offset-4"
                href="#make-room-for-evidence"
              >
                {t(guideMessages.tocThree)}
              </a>
            </li>
          </ol>
        </nav>

        <div className="space-y-16 text-base leading-relaxed text-muted-foreground">
          <GuideSection id="read-the-job-description" heading={guideMessages.sectionOneHeading}>
            <div className="space-y-4">
              <p>{t(guideMessages.sectionOneParagraphOne)}</p>
              <p>
                {t(guideMessages.sectionOneParagraphTwo)}{" "}
                <a
                  className="font-medium text-foreground underline underline-offset-4"
                  href={sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t(guideMessages.sectionOneSourceLink)}
                </a>
              </p>
              <p className="text-sm text-muted-foreground">{t(guideMessages.sectionOneSourceNote)}</p>
            </div>

            <div className="space-y-3">
              <div className="hidden gap-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase md:grid md:grid-cols-[minmax(10rem,0.8fr)_minmax(0,1fr)_minmax(0,1fr)]">
                <span>{t(guideMessages.tableHeaderWork)}</span>
                <span>{t(guideMessages.tableHeaderMeaning)}</span>
                <span>{t(guideMessages.tableHeaderEvidence)}</span>
              </div>
              {rows.map(([label, meaning, evidence]) => (
                <div
                  key={label.id}
                  className="grid gap-2 rounded-lg border border-border bg-card p-4 md:grid-cols-[minmax(10rem,0.8fr)_minmax(0,1fr)_minmax(0,1fr)] md:gap-3"
                >
                  <p className="font-semibold text-foreground">{t(label)}</p>
                  <p>{t(meaning)}</p>
                  <p>{t(evidence)}</p>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <p>{t(guideMessages.sectionOneParagraphThree)}</p>
              <p>{t(guideMessages.sectionOneParagraphFour)}</p>
              <p>{t(guideMessages.sectionOneParagraphFive)}</p>
            </div>
          </GuideSection>

          <GuideSection id="rewrite-relevant-experience" heading={guideMessages.sectionTwoHeading}>
            <p>{t(guideMessages.sectionTwoParagraphOne)}</p>

            <div className="grid gap-4 md:grid-cols-2">
              <ComparisonCard currentI18n={currentI18n} label={guideMessages.comparisonBeforeLabel} tone="muted">
                <blockquote className="whitespace-pre-line">{t(guideMessages.comparisonBefore)}</blockquote>
              </ComparisonCard>
              <ComparisonCard currentI18n={currentI18n} label={guideMessages.comparisonAfterLabel} tone="accent">
                <ul className="list-disc space-y-3 pl-5">
                  <li>{t(guideMessages.comparisonAfterOne)}</li>
                  <li>{t(guideMessages.comparisonAfterTwo)}</li>
                </ul>
              </ComparisonCard>
            </div>

            <div className="space-y-4">
              <p>{t(guideMessages.sectionTwoParagraphTwo)}</p>
              <p>{t(guideMessages.sectionTwoDetailOne)}</p>
              <p>{t(guideMessages.sectionTwoDetailTwo)}</p>
              <p>{t(guideMessages.sectionTwoDetailThree)}</p>
              <p>{t(guideMessages.sectionTwoParagraphThree)}</p>
              <p>{t(guideMessages.sectionTwoParagraphFour)}</p>
              <p>{t(guideMessages.sectionTwoParagraphFive)}</p>
              <p>{t(guideMessages.sectionTwoParagraphSix)}</p>
            </div>
          </GuideSection>

          <GuideSection id="make-room-for-evidence" heading={guideMessages.sectionThreeHeading}>
            <div className="space-y-4">
              <p>{t(guideMessages.sectionThreeParagraphOne)}</p>
              <p>{t(guideMessages.sectionThreeParagraphTwo)}</p>
              <p>{t(guideMessages.sectionThreeParagraphThree)}</p>
              <p>{t(guideMessages.sectionThreeParagraphFour)}</p>
              <p>{t(guideMessages.sectionThreeParagraphFive)}</p>
            </div>

            <div className="space-y-4 rounded-lg border border-border bg-muted/20 p-5">
              <h3 className="text-lg font-semibold text-foreground">{t(guideMessages.checklistHeading)}</h3>
              <ul className="list-disc space-y-3 pl-5">
                {[
                  guideMessages.checklistOne,
                  guideMessages.checklistTwo,
                  guideMessages.checklistThree,
                  guideMessages.checklistFour,
                  guideMessages.checklistFive,
                  guideMessages.checklistSix,
                ].map((item) => (
                  <li key={item.id}>{t(item)}</li>
                ))}
              </ul>
            </div>

            <details
              className="group rounded-lg border border-border bg-card"
              role="group"
              aria-label={t(guideMessages.fictionalResumeLabel)}
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                <span>{t(guideMessages.fictionalResumeLabel)}</span>
                <span
                  aria-hidden="true"
                  className="text-xl text-muted-foreground transition-transform group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <div className="space-y-6 border-t border-border p-5 text-sm leading-relaxed sm:p-8">
                <p className="text-muted-foreground">{t(guideMessages.fictionalResumeNote)}</p>
                {isEnglish ? <EnglishResume currentI18n={currentI18n} /> : <ChineseResume currentI18n={currentI18n} />}
              </div>
            </details>

            <p>
              {t(guideMessages.relatedManagePrefix)}
              {isEnglish ? " " : ""}
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href={MANAGE_RESUME_VERSIONS_GUIDE_PATH}
                data-phase6-entry="c01-to-c02"
              >
                {t(guideMessages.relatedManageLink)}
              </a>
              {isEnglish ? " " : ""}
              {t(guideMessages.relatedManageSuffix)}
            </p>
            <p>
              {t(guideMessages.relatedFactCheckPrefix)}
              {isEnglish ? " " : ""}
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href={CHECK_AI_RESUME_EDITS_GUIDE_PATH}
                data-phase6-entry="c01-to-c03"
              >
                {t(guideMessages.relatedFactCheckLink)}
              </a>
              {isEnglish ? " " : ""}
              {t(guideMessages.relatedFactCheckSuffix)}
            </p>

            <p>
              {t(guideMessages.productCall)}{" "}
              <a
                className="font-medium text-foreground underline underline-offset-4"
                href="/"
                data-phase6-entry="article-to-home"
              >
                {t(guideMessages.productHomeLink)}
              </a>
            </p>
          </GuideSection>
        </div>
      </article>

      <PublicFooter />
    </main>
  );
}

export type { GuideCopy };
